import {
  LeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  HolderOutlined,
  RobotOutlined,
  FolderOpenOutlined,
  UpOutlined,
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Tabs,
  Tooltip,
  Collapse,
} from "antd";
import { AdminFileManager, buildMediaFullUrl, StickyFormActions } from "@components";
import {
  Api,
  canManageContentActivation,
  CHAT_LESSON_TOPIC_OPTIONS,
  IChatLesson,
  IChatLessonCourseTemplate,
  IChatLessonStep,
  IChatLessonStepMetadata,
  TtsVoice,
} from "@model";
import {
  CHAT_LESSON_LANGUAGES,
  cleanChatLessonMetadata,
  cleanStepMetadata,
  getConfiguredLessonLanguageKeys,
  legacyLanguageKey,
  normalizeChatLessonMetadata,
  normalizeStepMetadata,
  resolveLessonListTitleFromMetadata,
  voiceCodeForLanguageKey,
} from "./chatLessonLanguages";
import {
  COURSE_TEMPLATE_LANGUAGE_PREVIEW_KEYS,
  COURSE_TEMPLATE_PLACEHOLDER_KEYS,
  COURSE_TEMPLATE_PLACEHOLDERS_HINT_STYLE,
  COURSE_TEMPLATE_PLACEHOLDERS_TEXT,
} from "./courseTemplatePlaceholders";
import {
  useCallback,
  useEffect,
  useState,
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";

const { Panel } = Collapse;
type SizedImageUrls = { sm?: string; md?: string };
type LessonStepLevelBucketLike = {
  user?: string[];
  ai?: string[];
};

type LessonStepLike = {
  type?: string;
  stepId?: string;
  title?: string;
  character?: string;
  context?: string;
  courseTemplateId?: string;
  tasksByLevel?: {
    beginner?: LessonStepLevelBucketLike | string[];
    intermediate?: LessonStepLevelBucketLike | string[];
    advanced?: LessonStepLevelBucketLike | string[];
  };
};

type CourseLevelKey = "beginner" | "intermediate" | "advanced";

const METADATA_SECTION_STYLE: CSSProperties = {
  background: "#f1f1f1",
  border: "1px solid #f0f0f0",
  borderRadius: 8,
  padding: "12px 16px 4px",
  marginBottom: 16,
};

const METADATA_SECTION_LABEL_STYLE: CSSProperties = {
  marginBottom: 10,
  fontSize: 12,
  fontWeight: 600,
  color: "rgba(0, 0, 0, 0.55)",
  letterSpacing: 0.3,
};

const toStoredMediaPath = (fullUrl: string): string => {
  try {
    return new URL(fullUrl).pathname.replace(/^\//, "");
  } catch {
    return fullUrl.replace(/^https?:\/\/[^/]+\//, "").replace(/^\//, "");
  }
};

const mediaPreviewSrc = (path?: string) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return buildMediaFullUrl(path);
};

type FileManagerTarget =
  | { type: "thumbnail" }
  | { type: "avatar" }
  | { type: "stepAvatar"; stepIndex: number };

const DEFAULT_XP_REWARD_BY_LEVEL = {
  beginner: 10,
  intermediate: 18,
  advanced: 25,
} as const;

function normalizeXpRewardByLevel(
  data: Partial<IChatLesson> & Record<string, unknown>,
): IChatLesson["xpRewardByLevel"] {
  const raw = data.xpRewardByLevel as Record<string, unknown> | undefined;
  const legacy =
    typeof data.xpReward === "number" && Number.isFinite(data.xpReward)
      ? Math.max(0, Math.round(data.xpReward))
      : undefined;
  const pick = (key: CourseLevelKey): number => {
    const v = raw?.[key];
    if (typeof v === "number" && Number.isFinite(v)) {
      return Math.max(0, Math.round(v));
    }
    if (legacy != null) return legacy;
    return DEFAULT_XP_REWARD_BY_LEVEL[key];
  };
  return {
    beginner: pick("beginner"),
    intermediate: pick("intermediate"),
    advanced: pick("advanced"),
  };
}

function migrateTasksLevelBucket(raw: unknown): LessonStepLevelBucketLike {
  const cleanLines = (a: unknown): string[] =>
    Array.isArray(a)
      ? a.map((x) => String(x ?? "").trim()).filter((x) => x.length > 0)
      : [];
  if (Array.isArray(raw)) {
    return { user: cleanLines(raw), ai: [] };
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    return {
      user: cleanLines(o.user),
      ai: cleanLines(o.ai ?? o.Ai),
    };
  }
  return { user: [], ai: [] };
}

function normalizeLessonStepsForForm(
  steps: IChatLessonStep[] | undefined,
): IChatLessonStep[] {
  if (!steps?.length) return [];
  return steps.map((s) => {
    const tbl = s.tasksByLevel;
    const extended = s as IChatLessonStep & {
      inputMode?: ("voice" | "text")[];
      /** Legacy field name */
      inputModes?: ("voice" | "text")[];
    };
    const inputTypesResolved =
      (Array.isArray(s.inputTypes) &&
        s.inputTypes.length > 0 &&
        s.inputTypes) ||
      (Array.isArray(extended.inputModes) &&
        extended.inputModes.length > 0 &&
        extended.inputModes) ||
      (Array.isArray(extended.inputMode) &&
        extended.inputMode.length > 0 &&
        extended.inputMode) ||
      undefined;
    const {
      inputMode: _dropMode,
      inputModes: _dropModes,
      inputTypes: _dropTypes,
      ...rest
    } = s as IChatLessonStep & {
      inputMode?: unknown;
      inputModes?: unknown;
      inputTypes?: unknown;
    };
    return {
      ...rest,
      ...(inputTypesResolved ? { inputTypes: inputTypesResolved } : {}),
      tasksByLevel: {
        beginner: migrateTasksLevelBucket(tbl?.beginner),
        intermediate: migrateTasksLevelBucket(tbl?.intermediate),
        advanced: migrateTasksLevelBucket(tbl?.advanced),
      },
    };
  });
}

function tasksLinesForPreviewLevel(
  step: LessonStepLike | undefined,
  level: CourseLevelKey,
  kind: "user" | "ai",
): string[] | undefined {
  const tbl = step?.tasksByLevel;
  if (!tbl) return undefined;

  const linesFromBucket = (
    bucket: LessonStepLevelBucketLike | string[] | undefined,
  ): string[] | undefined => {
    if (!bucket) return undefined;
    if (Array.isArray(bucket)) {
      if (kind !== "user") return undefined;
      const arr = bucket
        .map((t) => String(t ?? "").trim())
        .filter((t) => t.length > 0);
      return arr.length ? arr : undefined;
    }
    const raw = kind === "user" ? bucket.user : bucket.ai;
    if (!Array.isArray(raw)) return undefined;
    const arr = raw
      .map((t) => String(t ?? "").trim())
      .filter((t) => t.length > 0);
    return arr.length ? arr : undefined;
  };

  const directLines = linesFromBucket(tbl[level]);
  if (directLines?.length) return directLines;

  const order: CourseLevelKey[] =
    level === "advanced"
      ? ["advanced", "intermediate", "beginner"]
      : level === "intermediate"
        ? ["intermediate", "beginner", "advanced"]
        : ["beginner", "intermediate", "advanced"];
  for (const k of order) {
    const lines = linesFromBucket(tbl[k]);
    if (lines?.length) return lines;
  }
  return undefined;
}

function migrateStepForForm(
  step: IChatLessonStep,
  primaryLang: string,
): IChatLessonStep {
  const legacy = step as IChatLessonStep & {
    title?: string;
    voiceName?: string;
  };
  let metadata = normalizeStepMetadata(step.metadata);
  const legacyTitle = legacy.title?.trim();
  const legacyVoice = legacy.voiceName?.trim();
  if (legacyTitle && !metadata[primaryLang]?.title) {
    metadata[primaryLang] = {
      ...metadata[primaryLang],
      title: legacyTitle,
    };
  }
  if (legacyVoice && !metadata[primaryLang]?.voiceName) {
    metadata[primaryLang] = {
      ...metadata[primaryLang],
      voiceName: legacyVoice,
    };
  }
  const legacyStarter = step.starterText?.trim();
  if (legacyStarter && !metadata[primaryLang]?.starterText?.trim()) {
    metadata[primaryLang] = {
      ...metadata[primaryLang],
      starterText: legacyStarter,
    };
  }
  const {
    title: _title,
    voiceName: _voiceName,
    starterText: _starterText,
    ...rest
  } = legacy;
  return { ...rest, metadata };
}

/** Map legacy fields into metadata and chat-only steps. */
function normalizeChatLessonForForm(
  data: Partial<IChatLesson> & Record<string, unknown>,
): Partial<IChatLesson> {
  const legacyLessonInputModes = Array.isArray(data.inputMode)
    ? (data.inputMode as ("voice" | "text")[])
    : undefined;
  const rawSteps = Array.isArray(data.steps)
    ? [...(data.steps as unknown[])]
    : [];
  let introduction =
    typeof data.introduction === "string" ? data.introduction : undefined;

  const introStep = rawSteps.find(
    (s: unknown) =>
      s &&
      typeof s === "object" &&
      String((s as { type?: string }).type ?? "").toLowerCase() ===
        "introduction",
  ) as { content?: string } | undefined;

  if (!introduction && introStep && typeof introStep.content === "string") {
    introduction = introStep.content;
  }

  const legacyLang = legacyLanguageKey(
    typeof data.language === "string" ? data.language : undefined,
  );
  let metadata = normalizeChatLessonMetadata(
    data.metadata as IChatLesson["metadata"],
  );
  const panelTitle = typeof data.title === "string" ? data.title.trim() : "";
  const legacyDescription =
    typeof data.description === "string" ? data.description.trim() : "";
  if (panelTitle && !metadata.en?.title?.trim()) {
    metadata.en = { ...metadata.en, title: panelTitle };
  }
  const hasMetadataTitles = Object.values(metadata).some((entry) =>
    entry?.title?.trim(),
  );
  if (panelTitle && !hasMetadataTitles && !metadata[legacyLang]?.title) {
    metadata[legacyLang] = { ...metadata[legacyLang], title: panelTitle };
  }
  if (legacyDescription && !metadata[legacyLang]?.description) {
    metadata[legacyLang] = {
      ...metadata[legacyLang],
      description: legacyDescription,
    };
  }

  const primaryLang =
    getConfiguredLessonLanguageKeys(metadata)[0] ?? legacyLang;

  const chatLikeSteps = rawSteps.filter((s: unknown) => {
    if (!s || typeof s !== "object") return false;
    const t = String((s as { type?: string }).type ?? "").toLowerCase();
    return t !== "introduction" && t !== "feedback";
  });

  const mappedSteps = chatLikeSteps.map((s: unknown) => {
    if (!s || typeof s !== "object") {
      return { type: "chat" as const };
    }
    const o = s as Record<string, unknown>;
    const { goal: _goal, type: _stepType, ...rest } = o;
    return { ...rest, type: "chat" as const };
  }) as IChatLessonStep[];

  const steps = normalizeLessonStepsForForm(mappedSteps).map((step) =>
    migrateStepForForm(step, primaryLang),
  );

  const legacyLessonContext =
    typeof data.context === "string" && data.context.trim().length > 0
      ? data.context.trim()
      : undefined;
  const stepsWithMigratedContext =
    legacyLessonContext && steps.length > 0
      ? steps.map((s, i) =>
          i === 0 && !String(s.context ?? "").trim()
            ? { ...s, context: legacyLessonContext }
            : s,
        )
      : steps;

  const stepsWithInputTypes =
    legacyLessonInputModes?.length && stepsWithMigratedContext.length > 0
      ? stepsWithMigratedContext.map((s) =>
          !s.inputTypes?.length
            ? { ...s, inputTypes: [...legacyLessonInputModes] }
            : s,
        )
      : stepsWithMigratedContext;

  const {
    description: _description,
    language: _language,
    introductionVoiceUrl: _introductionVoiceUrl,
    context: _oldLessonContext,
    inputMode: _dropLessonInputMode,
    title: _panelTitle,
    ...restData
  } = data;

  return {
    ...restData,
    metadata,
    xpRewardByLevel: normalizeXpRewardByLevel(data),
    introduction,
    steps: stepsWithInputTypes,
  };
}

type MetadataTranslation = Record<
  string,
  { title?: string; description?: string; starterText?: string }
>;

function mergeMetadataTranslations<
  T extends {
    title?: string;
    description?: string;
    starterText?: string;
    voiceName?: string;
  },
>(current: Record<string, T> | undefined, translations: MetadataTranslation) {
  const next = { ...(current ?? {}) };
  for (const [langKey, fields] of Object.entries(translations)) {
    if (langKey === "en" || !fields) continue;
    next[langKey] = {
      ...next[langKey],
      ...(fields.title?.trim() ? { title: fields.title.trim() } : {}),
      ...(fields.description?.trim()
        ? { description: fields.description.trim() }
        : {}),
      ...(fields.starterText?.trim()
        ? { starterText: fields.starterText.trim() }
        : {}),
    };
  }
  return next;
}

function renderFileManagerButton(onClick: () => void) {
  return (
    <Tooltip title="Select from file manager">
      <Button
        type="default"
        icon={<FolderOpenOutlined />}
        aria-label="Select from file manager"
        onClick={onClick}
      />
    </Tooltip>
  );
}

function renderMetadataTranslateButton(options: {
  loading: boolean;
  onClick: () => void;
  tooltip: string;
}) {
  return (
    <Tooltip title={options.tooltip}>
      <Button
        type="text"
        aria-label={options.tooltip}
        icon={<RobotOutlined />}
        loading={options.loading}
        onClick={options.onClick}
      />
    </Tooltip>
  );
}

function languageCodeToLabel(code: string): string {
  const c = code.trim().toLowerCase();
  if (!c) return "";
  const hit = CHAT_LESSON_LANGUAGES.find(
    (lang) =>
      lang.voiceCode.toLowerCase() === c ||
      lang.key === c ||
      lang.key === c.split("-")[0],
  );
  return hit?.label ?? code;
}

function renderHighlightedCourseTemplatePreview(
  raw: string,
  vars: Record<string, string>,
): ReactNode {
  const re = new RegExp(
    `\\{\\{\\s*(${COURSE_TEMPLATE_PLACEHOLDER_KEYS})\\s*\\}\\}`,
    "g",
  );
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    if (match.index > last) {
      parts.push(raw.slice(last, match.index));
    }
    const key = match[1] as string;
    const value = vars[key] ?? "";
    const resolved =
      COURSE_TEMPLATE_LANGUAGE_PREVIEW_KEYS.has(key) && value.length > 0
        ? languageCodeToLabel(value)
        : value;
    const display = resolved.length > 0 ? resolved : `{{${key}}}`;
    parts.push(
      <span
        key={`ph-${match.index}-${key}`}
        style={{
          background: "rgba(22, 119, 255, 0.12)",
          border: "1px solid rgba(22, 119, 255, 0.22)",
          borderRadius: 4,
          padding: "0 3px",
          color: value.length > 0 ? "#0958d9" : "rgba(0,0,0,0.38)",
        }}
      >
        {display}
      </span>,
    );
    last = match.index + match[0].length;
  }
  if (last < raw.length) {
    parts.push(raw.slice(last));
  }
  return parts.length > 0 ? parts : raw;
}

function readStepEnMetadata(
  form: ReturnType<typeof Form.useForm>[0],
  stepIndex: number,
  step?: IChatLessonStep,
) {
  const read = (field: "title" | "starterText") =>
    String(
      form.getFieldValue(["steps", stepIndex, "metadata", "en", field]) ??
        step?.metadata?.en?.[field] ??
        (field === "starterText" ? step?.starterText : undefined) ??
        "",
    ).trim();

  return {
    title: read("title"),
    starterText: read("starterText"),
  };
}

function clampAiGeneratePanelPosition(
  left: number,
  top: number,
  width: number,
  height: number,
): { left: number; top: number } {
  const margin = 8;
  const maxLeft = Math.max(margin, window.innerWidth - width - margin);
  const maxTop = Math.max(margin, window.innerHeight - height - margin);
  return {
    left: Math.min(Math.max(margin, left), maxLeft),
    top: Math.min(Math.max(margin, top), maxTop),
  };
}

function randomVoiceNameForLanguage(
  voices: TtsVoice[],
  languageCode: string,
): string | undefined {
  const prefix = `${languageCode.trim().toLowerCase()}-`;
  const matching = voices.filter((voice) =>
    voice.name.trim().toLowerCase().startsWith(prefix),
  );
  if (matching.length === 0) return undefined;
  return matching[Math.floor(Math.random() * matching.length)].name;
}

export default function ChatLessonCreatePage() {
  const { state: locationState } = useLocation();
  const returnTo =
    locationState &&
    typeof locationState === "object" &&
    "returnTo" in locationState
      ? (locationState as { returnTo?: string }).returnTo
      : undefined;
  const state =
    locationState &&
    typeof locationState === "object" &&
    "returnTo" in locationState
      ? (() => {
          const { returnTo: _, ...rest } = locationState as {
            returnTo?: string;
            [k: string]: unknown;
          };
          return rest as unknown as IChatLesson | undefined;
        })()
      : (locationState as IChatLesson | undefined);
  const { id } = useParams<{ id: string }>();
  const isMounted = useRef(true);
  const initKeyRef = useRef<string | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [currentLesson, setCurrentLesson] = useState<IChatLesson | undefined>(
    state,
  );
  const [voicesByLang, setVoicesByLang] = useState<Record<string, TtsVoice[]>>(
    {},
  );
  const [loadingVoicesByLang, setLoadingVoicesByLang] = useState<
    Record<string, boolean>
  >({});
  const [playingStepVoiceIndex, setPlayingStepVoiceIndex] = useState<
    number | null
  >(null);
  const [sameVoiceAcrossSteps, setSameVoiceAcrossSteps] = useState<
    Record<number, boolean>
  >({});
  const [activeLanguageTab, setActiveLanguageTab] = useState<string>(
    CHAT_LESSON_LANGUAGES[0].key,
  );
  const [thumbnail, setThumbnail] = useState<SizedImageUrls>();
  const [avatar, setAvatar] = useState<SizedImageUrls>();
  const [fileManagerOpen, setFileManagerOpen] = useState(false);
  const [fileManagerTarget, setFileManagerTarget] =
    useState<FileManagerTarget | null>(null);
  const [courseTemplates, setCourseTemplates] = useState<
    IChatLessonCourseTemplate[]
  >([]);
  const [loadingCourseTemplates, setLoadingCourseTemplates] = useState(false);
  const [templateDraft, setTemplateDraft] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewTaskLevel, setPreviewTaskLevel] =
    useState<CourseLevelKey>("beginner");
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [activeStepLanguageTabByStep, setActiveStepLanguageTabByStep] =
    useState<Record<number, string>>({});
  const [isPreviewPanelOpen, setIsPreviewPanelOpen] = useState(false);
  const isCreateMode = !id && !state?._id;
  const canManageActivation = canManageContentActivation();
  /** Avoid re-seeding after user cleared steps; only for fresh create. */
  const defaultFirstStepSeededRef = useRef(false);

  const [generating, setGenerating] = useState(false);
  const [aiGeneratePanelOpen, setAiGeneratePanelOpen] = useState(false);
  const [aiGeneratePanelExpanded, setAiGeneratePanelExpanded] = useState(true);
  const [aiGenerateTitle, setAiGenerateTitle] = useState("");
  const [aiGenerateDescription, setAiGenerateDescription] = useState("");
  const [aiGenerateDescriptionRtl, setAiGenerateDescriptionRtl] =
    useState(false);
  const [aiGeneratePanelPos, setAiGeneratePanelPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [aiGeneratePanelDragging, setAiGeneratePanelDragging] = useState(false);
  const aiGeneratePanelRef = useRef<HTMLDivElement>(null);
  const aiGenerateDragRef = useRef<{
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
    moved: boolean;
  } | null>(null);
  const [translatingLessonMetadata, setTranslatingLessonMetadata] =
    useState(false);
  const [translatingStepMetadataIndex, setTranslatingStepMetadataIndex] =
    useState<number | null>(null);

  const fetchVoicesForLanguage = useCallback(
    async (voiceCode: string): Promise<TtsVoice[]> => {
      if (!voiceCode) return [];
      setLoadingVoicesByLang((prev) => ({ ...prev, [voiceCode]: true }));
      try {
        const response = await Api.get(
          `/api/tts/voices?languageCode=${voiceCode}`,
        );
        const voices = (response.data?.data ?? []) as TtsVoice[];
        if (isMounted.current) {
          setVoicesByLang((prev) => ({
            ...prev,
            [voiceCode]: voices,
          }));
        }
        return voices;
      } catch {
        if (isMounted.current) {
          message.error(`Failed to fetch voices for ${voiceCode}`);
          setVoicesByLang((prev) => ({ ...prev, [voiceCode]: [] }));
        }
        return [];
      } finally {
        if (isMounted.current) {
          setLoadingVoicesByLang((prev) => ({ ...prev, [voiceCode]: false }));
        }
      }
    },
    [],
  );

  const seedAiGenerateFields = useCallback(() => {
    const formValues = form.getFieldsValue(true);
    setAiGenerateTitle(String(formValues.aiGenerateTitle ?? "").trim());
    setAiGenerateDescription(
      String(formValues.aiGenerateDescription ?? "").trim(),
    );
  }, [form]);

  const openAiGeneratePanel = () => {
    seedAiGenerateFields();
    setAiGeneratePanelOpen(true);
    setAiGeneratePanelExpanded(true);
  };

  const handleAiGenerateHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if ((event.target as HTMLElement).closest("button")) return;
    const panel = aiGeneratePanelRef.current;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    const originLeft = aiGeneratePanelPos?.left ?? rect.left;
    const originTop = aiGeneratePanelPos?.top ?? rect.top;

    if (!aiGeneratePanelPos) {
      setAiGeneratePanelPos({ left: rect.left, top: rect.top });
    }

    aiGenerateDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originLeft,
      originTop,
      moved: false,
    };
    setAiGeneratePanelDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleAiGenerateHeaderPointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    const drag = aiGenerateDragRef.current;
    const panel = aiGeneratePanelRef.current;
    if (!drag || !panel) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 4) return;

    drag.moved = true;
    const { width, height } = panel.getBoundingClientRect();
    setAiGeneratePanelPos(
      clampAiGeneratePanelPosition(
        drag.originLeft + dx,
        drag.originTop + dy,
        width,
        height,
      ),
    );
  };

  const handleAiGenerateHeaderPointerUp = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    const drag = aiGenerateDragRef.current;
    aiGenerateDragRef.current = null;
    setAiGeneratePanelDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (!drag?.moved && !aiGeneratePanelExpanded) {
      setAiGeneratePanelExpanded(true);
    }
  };

  const handleAiGenerate = async () => {
    const enTitle = aiGenerateTitle.trim();
    const enDescription = aiGenerateDescription.trim();
    const language = voiceCodeForLanguageKey("en");
    const languageKeys = CHAT_LESSON_LANGUAGES.map((lang) => lang.key);

    if (!enTitle) {
      message.error("Please enter a lesson title for AI generation");
      return;
    }

    const formValues = form.getFieldsValue(true);
    const isEditingGeneratedLesson = Boolean(
      String(formValues.aiGenerateTitle ?? "").trim() ||
        String(formValues.aiGenerateDescription ?? "").trim(),
    );
    const existingLesson = isEditingGeneratedLesson
      ? {
          title: formValues.title,
          metadata: formValues.metadata,
          topic: formValues.topic,
          estimatedDuration: formValues.estimatedDuration,
          xpRewardByLevel: formValues.xpRewardByLevel,
          introduction: formValues.introduction,
          avatarPrompt: formValues.avatarPrompt,
          steps: formValues.steps,
        }
      : undefined;

    setGenerating(true);
    try {
      const courseTemplate =
        selectedTemplate?.template ?? templateDraft ?? undefined;
      const response = await Api.post(
        "/api/admin/panel/chat-lessons/generate",
        {
          title: enTitle,
          language,
          languageKey: "en",
          languageKeys,
          ...(enDescription ? { description: enDescription } : {}),
          ...(courseTemplate ? { courseTemplate } : {}),
          ...(existingLesson ? { existingLesson } : {}),
        },
      );
      const generated = response.data?.data;
      if (!generated) {
        message.error("AI returned empty data");
        return;
      }
      const normalized = normalizeChatLessonForForm(
        generated as Parameters<typeof normalizeChatLessonForForm>[0],
      );
      const voicesForLanguages = await Promise.all(
        CHAT_LESSON_LANGUAGES.map(async (lang) => [
          lang.key,
          await fetchVoicesForLanguage(lang.voiceCode),
        ]),
      );
      const voiceNameByLanguage = Object.fromEntries(
        voicesForLanguages.map(([languageKey, voices]) => [
          languageKey,
          randomVoiceNameForLanguage(
            voices as TtsVoice[],
            voiceCodeForLanguageKey(languageKey as string),
          ),
        ]),
      ) as Record<string, string | undefined>;
      const defaultTemplateId = courseTemplates[0]?._id;
      const generatedSteps = (normalized.steps ?? []).map((step) => {
        const metadata = normalizeStepMetadata(step.metadata);
        const metadataWithVoices = { ...metadata };
        for (const lang of CHAT_LESSON_LANGUAGES) {
          const voiceName = voiceNameByLanguage[lang.key];
          const { starterText: _starterText, ...langMeta } = {
            ...metadataWithVoices[lang.key],
          };
          metadataWithVoices[lang.key] = voiceName
            ? { ...langMeta, voiceName }
            : langMeta;
        }
        return {
          ...step,
          ...(defaultTemplateId
            ? { courseTemplateId: defaultTemplateId }
            : {}),
          metadata: metadataWithVoices,
        };
      });
      form.setFieldsValue({
        ...normalized,
        steps: generatedSteps,
        aiGenerateTitle: enTitle,
        aiGenerateDescription: enDescription,
      });
      setActiveLanguageTab("en");
      defaultFirstStepSeededRef.current = true;
      message.success("Lesson generated — review and save when ready");
    } catch {
      message.error("Failed to generate lesson");
    } finally {
      if (isMounted.current) setGenerating(false);
    }
  };

  const requestMetadataTranslations = async (payload: {
    title: string;
    description?: string;
    starterText?: string;
  }) => {
    const response = await Api.post(
      "/api/admin/panel/chat-lessons/translate-metadata",
      payload,
    );
    return response.data?.data as MetadataTranslation | undefined;
  };

  const handleTranslateLessonMetadata = async () => {
    const formValues = form.getFieldsValue(true);
    const metadata = (formValues.metadata ?? {}) as IChatLesson["metadata"];
    const enMeta = metadata?.en ?? {};

    const enTitle = String(enMeta.title ?? "").trim();
    const enDescription = String(enMeta.description ?? "").trim();

    if (!enTitle) {
      message.error("Fill in the English app title first");
      return;
    }

    setTranslatingLessonMetadata(true);
    try {
      const translations = await requestMetadataTranslations({
        title: enTitle,
        ...(enDescription ? { description: enDescription } : {}),
      });
      if (!translations || !isMounted.current) {
        message.error("AI returned no translations");
        return;
      }

      form.setFieldsValue({
        metadata: mergeMetadataTranslations(metadata, translations),
      });
      message.success("Translated lesson metadata from English");
    } catch {
      if (isMounted.current) message.error("Failed to translate metadata");
    } finally {
      if (isMounted.current) setTranslatingLessonMetadata(false);
    }
  };

  const handleTranslateStepMetadata = async (stepIndex: number) => {
    const formValues = form.getFieldsValue(true);
    const steps = (formValues.steps ?? []) as IChatLesson["steps"];
    const step = steps?.[stepIndex];
    const stepMetadata = (step?.metadata ?? {}) as IChatLessonStepMetadata;
    const { title: enTitle, starterText: enStarterText } = readStepEnMetadata(
      form,
      stepIndex,
      step,
    );

    if (!enTitle) {
      message.error("Fill in the English step title first");
      return;
    }

    setTranslatingStepMetadataIndex(stepIndex);
    try {
      const translations = await requestMetadataTranslations({
        title: enTitle,
        ...(enStarterText ? { starterText: enStarterText } : {}),
      });
      if (!translations || !isMounted.current) {
        message.error("AI returned no translations");
        return;
      }

      const merged = mergeMetadataTranslations(stepMetadata, translations);
      form.setFieldValue(["steps", stepIndex, "metadata"], merged);
      message.success("Translated step metadata from English");
    } catch {
      if (isMounted.current) message.error("Failed to translate step metadata");
    } finally {
      if (isMounted.current) setTranslatingStepMetadataIndex(null);
    }
  };

  const navigate = useNavigate();
  const watchedValues = Form.useWatch([], form) as
    | Partial<IChatLesson>
    | undefined;
  useEffect(() => {
    const steps = (watchedValues?.steps as LessonStepLike[] | undefined) ?? [];
    if (steps.length === 0) {
      if (activeStepIndex !== 0) setActiveStepIndex(0);
      return;
    }
    if (activeStepIndex > steps.length - 1) {
      setActiveStepIndex(steps.length - 1);
    }
  }, [watchedValues?.steps, activeStepIndex]);

  const courseTemplateOptions = courseTemplates.map((t) => ({
    value: t._id,
    label: t.name,
  }));

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (id) {
      if (initKeyRef.current === id) return;
      initKeyRef.current = id;
      Api.get(`/api/admin/panel/chat-lessons/${id}`)
        .then(async (response) => {
          if (!isMounted.current || !response.data.data) return;
          const lessonData = response.data.data;

          if (!isMounted.current) return;
          const normalized = normalizeChatLessonForForm(
            lessonData as Partial<IChatLesson> & Record<string, unknown>,
          );
          setCurrentLesson(normalized as IChatLesson);
          form.setFieldsValue(normalized);
          const configuredKeys = getConfiguredLessonLanguageKeys(
            normalized.metadata,
          );
          if (configuredKeys.length > 0) {
            setActiveLanguageTab(configuredKeys[0]);
          }
          if (lessonData.thumbnail) setThumbnail(lessonData.thumbnail);
          if (lessonData.avatar) setAvatar(lessonData.avatar);
        })
        .catch(() => {
          if (isMounted.current) message.error("Failed to load lesson data");
        });
    } else if (state && !(state as any).returnTo) {
      const key = "create";
      if (initKeyRef.current === key) return;
      initKeyRef.current = key;
      const normalized = normalizeChatLessonForForm(
        state as Partial<IChatLesson> & Record<string, unknown>,
      );
      setCurrentLesson(normalized as IChatLesson);
      form.setFieldsValue(normalized);
      const configuredKeys = getConfiguredLessonLanguageKeys(
        normalized.metadata,
      );
      if (configuredKeys.length > 0) {
        setActiveLanguageTab(configuredKeys[0]);
      }
      if (state.thumbnail) setThumbnail(state.thumbnail);
      if (state.avatar) setAvatar(state.avatar);
    }
  }, [id, form, state]);

  useEffect(() => {
    CHAT_LESSON_LANGUAGES.forEach((lang) => {
      fetchVoicesForLanguage(lang.voiceCode);
    });
  }, [fetchVoicesForLanguage]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCourseTemplates(true);
    Api.get("/api/admin/panel/chat-lesson-course-templates", {
      params: { limit: 500 },
    })
      .then((response) => {
        if (cancelled || !isMounted.current) return;
        const raw = response?.data?.data;
        setCourseTemplates(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!isMounted.current) return;
        message.error("Failed to load course templates");
        setCourseTemplates([]);
      })
      .finally(() => {
        if (isMounted.current) setLoadingCourseTemplates(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isCreateMode || defaultFirstStepSeededRef.current) return;
    if (id || state?._id) return;
    if (loadingCourseTemplates) return;
    if (courseTemplates.length === 0) return;

    const currentSteps = form.getFieldValue("steps") as
      | IChatLessonStep[]
      | undefined;
    if (Array.isArray(currentSteps) && currentSteps.length > 0) {
      defaultFirstStepSeededRef.current = true;
      return;
    }

    const courseTemplate = courseTemplates[0];

    form.setFieldsValue({
      steps: [
        {
          type: "chat" as const,
          metadata: {
            en: { title: "Step 1" },
          },
          ...(courseTemplate?._id
            ? { courseTemplateId: courseTemplate._id }
            : {}),
          tasksByLevel: {
            beginner: { user: [], ai: [] },
            intermediate: { user: [], ai: [] },
            advanced: { user: [], ai: [] },
          },
          inputTypes: ["voice", "text"],
        },
      ],
    });
    defaultFirstStepSeededRef.current = true;
  }, [
    isCreateMode,
    id,
    state?._id,
    courseTemplates,
    loadingCourseTemplates,
    form,
  ]);

  const activeVoiceCode = voiceCodeForLanguageKey(activeLanguageTab);

  const getStepLanguageTab = (stepIndex: number) =>
    activeStepLanguageTabByStep[stepIndex] ?? CHAT_LESSON_LANGUAGES[0].key;

  const setStepLanguageTab = (stepIndex: number, langKey: string) => {
    setActiveStepLanguageTabByStep((prev) => ({
      ...prev,
      [stepIndex]: langKey,
    }));
  };

  const getStepDisplayTitle = useCallback(
    (stepIndex: number): string => {
      const metadata = watchedValues?.steps?.[stepIndex]?.metadata;
      if (metadata) {
        for (const lang of CHAT_LESSON_LANGUAGES) {
          const title = metadata[lang.key]?.title?.trim();
          if (title) return title;
        }
      }
      return `Chat step ${stepIndex + 1}`;
    },
    [watchedValues?.steps],
  );

  const openFileManager = (target: FileManagerTarget) => {
    setFileManagerTarget(target);
    setFileManagerOpen(true);
  };

  const handleFileManagerSelect = (fullUrl: string) => {
    const relative = toStoredMediaPath(fullUrl);
    const sized = { md: relative, sm: relative };

    if (fileManagerTarget?.type === "thumbnail") {
      setThumbnail(sized);
      message.success("Thumbnail selected");
    } else if (fileManagerTarget?.type === "avatar") {
      setAvatar(sized);
      message.success("Avatar selected");
    } else if (fileManagerTarget?.type === "stepAvatar") {
      form.setFieldValue(
        ["steps", fileManagerTarget.stepIndex, "avatar"],
        sized,
      );
      message.success("Step avatar selected");
    }

    setFileManagerOpen(false);
    setFileManagerTarget(null);
  };

  const createOrUpdateLesson = async (values: IChatLesson) => {
    setSaving(true);
    try {
      const allFormValues = form.getFieldsValue(true);
      values.thumbnail = thumbnail;
      values.avatar = avatar;
      values.estimatedDuration = allFormValues.estimatedDuration;
      values.xpRewardByLevel = normalizeXpRewardByLevel(allFormValues);
      if (!canManageActivation) {
        delete (values as Partial<IChatLesson>).isActive;
      }

      values.introduction =
        typeof allFormValues.introduction === "string"
          ? allFormValues.introduction
          : undefined;

      const lessonMetadata = cleanChatLessonMetadata(
        allFormValues.metadata as IChatLesson["metadata"],
      );
      const lessonLangKeys = Object.keys(lessonMetadata);
      if (lessonLangKeys.length === 0) {
        message.error("Add a title for at least one language.");
        return;
      }
      values.metadata = lessonMetadata;
      const listTitle = resolveLessonListTitleFromMetadata(lessonMetadata);
      if (!listTitle) {
        message.error("Enter an English app title.");
        return;
      }
      values.title = listTitle;

      const cleanLines = (a: unknown): string[] =>
        Array.isArray(a)
          ? a
              .map((x: unknown) => String(x ?? "").trim())
              .filter((x) => x.length > 0)
          : [];

      const levelBucketFromRaw = (
        raw: unknown,
      ): { user: string[]; ai: string[] } => {
        if (Array.isArray(raw)) return { user: cleanLines(raw), ai: [] };
        if (raw && typeof raw === "object") {
          const o = raw as Record<string, unknown>;
          return {
            user: cleanLines(o.user),
            ai: cleanLines(o.ai ?? o.Ai),
          };
        }
        return { user: [], ai: [] };
      };

      const rawSteps = (allFormValues.steps || []).filter(
        (step: unknown) => step != null,
      );
      values.steps = rawSteps.map(
        (step: Record<string, unknown>, idx: number) => {
          const {
            goal: _g,
            type: _t,
            title: _legacyTitle,
            voiceName: _legacyVoiceName,
            tasksByLevel: rawTbl,
            inputMode: _legacyInputMode,
            inputModes: _legacyInputModes,
            inputTypes: _fromFormTypes,
            finisherMessage: _dropFinisherMessage,
            voiceUrl: _dropVoiceUrl,
            starterText: _legacyStarterText,
            metadata: rawMetadata,
            ...rest
          } = step;
          const tbl = (rawTbl ?? {}) as Record<string, unknown>;
          const rawModes =
            _fromFormTypes ?? _legacyInputModes ?? _legacyInputMode;
          let inputTypes: ("voice" | "text")[] = [];
          if (Array.isArray(rawModes)) {
            inputTypes = rawModes
              .map((x: unknown) =>
                String(x ?? "")
                  .trim()
                  .toLowerCase(),
              )
              .filter(
                (x): x is "voice" | "text" => x === "voice" || x === "text",
              );
          }
          if (inputTypes.length === 0) {
            inputTypes = ["voice", "text"];
          }

          const stepMetadata = cleanStepMetadata(
            rawMetadata as IChatLessonStepMetadata,
          );
          const hasTitle = Object.values(stepMetadata).some((entry) =>
            entry?.title?.trim(),
          );
          if (!hasTitle) {
            const fallbackLang = lessonLangKeys[0] ?? "en";
            stepMetadata[fallbackLang] = {
              ...stepMetadata[fallbackLang],
              title: `Step ${idx + 1}`,
            };
          }

          return {
            ...rest,
            type: "chat" as const,
            metadata: stepMetadata,
            starterText: stepMetadata.en?.starterText?.trim() || undefined,
            stepId:
              (typeof step.stepId === "string" && step.stepId.trim()) ||
              `step_${idx + 1}`,
            courseTemplateId:
              typeof step.courseTemplateId === "string" &&
              step.courseTemplateId.trim().length > 0
                ? step.courseTemplateId.trim()
                : undefined,
            context:
              typeof step.context === "string" && step.context.trim().length > 0
                ? step.context.trim()
                : undefined,
            tasksByLevel: {
              beginner: levelBucketFromRaw(tbl.beginner),
              intermediate: levelBucketFromRaw(tbl.intermediate),
              advanced: levelBucketFromRaw(tbl.advanced),
            },
            inputTypes,
          };
        },
      );

      const chatSteps = (values.steps ?? []).filter((s) => s.type === "chat");
      if (chatSteps.length === 0) {
        message.error("Add at least one chat step.");
        return;
      }
      const levelKeys = ["beginner", "intermediate", "advanced"] as const;
      for (let i = 0; i < chatSteps.length; i += 1) {
        const s = chatSteps[i];
        const tbl = s.tasksByLevel;
        if (!tbl) {
          message.error(`Chat step ${i + 1}: configure tasks by level.`);
          return;
        }
        for (const lk of levelKeys) {
          const bucket = tbl[lk];
          const userLen = cleanLines(bucket?.user).length;
          const aiLen = cleanLines(bucket?.ai).length;
          if (userLen === 0 || aiLen === 0) {
            message.error(
              `Chat step ${i + 1}: add at least one learner task and one AI rule for ${lk}.`,
            );
            return;
          }
        }
      }

      if (isCreateMode) {
        const response = await Api.post(
          "/api/admin/panel/chat-lessons",
          values,
        );
        if (isMounted.current && response.data.data) {
          message.success("Lesson Created Successfully");
          navigate(returnTo ?? "/panel/chat-lessons");
        } else {
          if (isMounted.current) {
            message.error("An error occurred while creating lesson");
          }
        }
      } else {
        const response = await Api.put(
          `/api/admin/panel/chat-lessons/${values._id ?? ""}`,
          values,
        );

        if (isMounted.current && response.status === 200) {
          message.success("Successfully Edited");
          navigate(returnTo ?? "/panel/chat-lessons");
        } else {
          if (isMounted.current) {
            message.error("An error occurred");
          }
        }
      }
    } catch (error) {
      if (isMounted.current) {
        message.error("An error occurred");
      }
    } finally {
      if (isMounted.current) {
        setSaving(false);
      }
    }
  };

  const applyVoiceToAllSteps = (langKey: string, voiceName?: string) => {
    const steps =
      (form.getFieldValue("steps") as Record<string, unknown>[] | undefined) ??
      [];
    steps.forEach((_, idx) => {
      form.setFieldValue(
        ["steps", idx, "metadata", langKey, "voiceName"],
        voiceName,
      );
    });
  };

  const handleStepVoiceChange = (
    stepIndex: number,
    langKey: string,
    voiceName?: string,
  ) => {
    if (sameVoiceAcrossSteps[stepIndex]) {
      applyVoiceToAllSteps(langKey, voiceName);
    }
  };

  const applyAllLanguagesVoiceToAllSteps = (stepIndex: number) => {
    CHAT_LESSON_LANGUAGES.forEach((lang) => {
      const currentVoice = form.getFieldValue([
        "steps",
        stepIndex,
        "metadata",
        lang.key,
        "voiceName",
      ]) as string | undefined;
      applyVoiceToAllSteps(lang.key, currentVoice);
    });
  };

  const handleSameVoiceToggle = (stepIndex: number, checked: boolean) => {
    setSameVoiceAcrossSteps((prev) => ({ ...prev, [stepIndex]: checked }));
    if (checked) {
      applyAllLanguagesVoiceToAllSteps(stepIndex);
    }
  };

  const playStepVoice = async (
    stepIndex: number,
    langKey: string,
    voiceFromUi?: string,
  ) => {
    const stepFormValue = form.getFieldValue(["steps", stepIndex]) as
      | Record<string, unknown>
      | undefined;
    const languageCode = voiceCodeForLanguageKey(langKey);
    const stepVoice = String(
      voiceFromUi ??
        (stepFormValue?.metadata as IChatLessonStepMetadata | undefined)?.[
          langKey
        ]?.voiceName ??
        watchedValues?.steps?.[stepIndex]?.metadata?.[langKey]?.voiceName ??
        "",
    ).trim();
    const stepTitle =
      form.getFieldValue([
        "steps",
        stepIndex,
        "metadata",
        langKey,
        "title",
      ]) || "Step";

    if (!languageCode && !stepVoice) {
      message.warning("Please select language and step voice first");
      return;
    }
    if (!languageCode) {
      message.warning("Please select language first");
      return;
    }
    if (!stepVoice) {
      message.warning("Please select step voice first");
      return;
    }

    setPlayingStepVoiceIndex(stepIndex);
    try {
      const response = await Api.post(
        "/api/tts/synthesize",
        {
          text: `${stepTitle}`,
          languageCode,
          voiceName: stepVoice,
          speakingRate: 1.0,
        },
        {
          responseType: "blob",
        },
      );

      if (isMounted.current && response.data) {
        const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
        };
        await audio.play();
      }
    } catch {
      if (isMounted.current) {
        message.error("Failed to play step voice");
      }
    } finally {
      if (isMounted.current) {
        setPlayingStepVoiceIndex(null);
      }
    }
  };

  const formatTasksList = (tasks: string[] | undefined): string => {
    if (!tasks?.length) return "";
    return tasks
      .map((t) => String(t ?? "").trim())
      .filter(Boolean)
      .map((t) => `- ${t}`)
      .join("\n");
  };

  const watchedSteps =
    (watchedValues?.steps as LessonStepLike[] | undefined) ?? [];
  const chatStep =
    watchedSteps[activeStepIndex] ??
    watchedSteps[0] ??
    watchedSteps.find((step) => step?.type === "chat");
  const activeStepTemplateId = String(
    form.getFieldValue(["steps", activeStepIndex, "courseTemplateId"]) ??
      chatStep?.courseTemplateId ??
      "",
  ).trim();
  const selectedTemplate = courseTemplates.find(
    (t) => t._id === activeStepTemplateId,
  );
  const previewVars = {
    character: chatStep?.character?.trim() ?? "",
    level: previewTaskLevel,
    levelRules: "",
    userOriginLanguage: "fa-ir",
    userTargetLanguage: activeVoiceCode,
    topic: watchedValues?.topic ?? "",
    context: chatStep?.context?.trim() ?? "",
    user_tasks: formatTasksList(
      tasksLinesForPreviewLevel(chatStep, previewTaskLevel, "user"),
    ),
    ai_tasks: formatTasksList(
      tasksLinesForPreviewLevel(chatStep, previewTaskLevel, "ai"),
    ),
  };

  const lastHydratedCourseTemplateId = useRef<string | undefined>();
  useEffect(() => {
    const templateId = activeStepTemplateId;
    if (!templateId) {
      setTemplateDraft("");
      lastHydratedCourseTemplateId.current = undefined;
      return;
    }
    const t = courseTemplates.find((x) => x._id === templateId);
    if (!t) return;

    if (lastHydratedCourseTemplateId.current !== templateId) {
      lastHydratedCourseTemplateId.current = templateId;
      setTemplateDraft(t.template ?? "");
    }
  }, [activeStepTemplateId, courseTemplates]);

  const handleSaveCourseTemplate = async () => {
    if (!selectedTemplate) return;
    const trimmed = templateDraft.trim();
    if (!trimmed) {
      message.warning("Template body cannot be empty");
      return;
    }
    setSavingTemplate(true);
    try {
      await Api.put(
        `/api/admin/panel/chat-lesson-course-templates/${selectedTemplate._id}`,
        {
          name: selectedTemplate.name,
          template: templateDraft,
        },
      );
      if (!isMounted.current) return;
      setCourseTemplates((prev) =>
        prev.map((t) =>
          t._id === selectedTemplate._id
            ? { ...t, template: templateDraft }
            : t,
        ),
      );
      message.success("Course template saved");
    } catch {
      if (isMounted.current) message.error("Failed to save course template");
    } finally {
      if (isMounted.current) setSavingTemplate(false);
    }
  };

  return (
    <Card>
      <Form
        initialValues={{
          isActive: false,
          isPremium: false,
          isForJourney: false,
          xpRewardByLevel: { ...DEFAULT_XP_REWARD_BY_LEVEL },
          ...currentLesson,
          metadata: normalizeChatLessonMetadata(currentLesson?.metadata),
        }}
        form={form}
        layout="vertical"
        onFinish={createOrUpdateLesson}
      >
        <StickyFormActions
          onBack={() => navigate(-1)}
          actions={
            <>
              <Tooltip title="Open AI lesson generator — describe the lesson and steps you want">
                <Button
                  size="large"
                  icon={<RobotOutlined />}
                  onClick={openAiGeneratePanel}
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderColor: "transparent",
                    color: "#fff",
                  }}
                >
                  AI Generate
                </Button>
              </Tooltip>
              <Button
                loading={saving}
                htmlType="submit"
                type="primary"
                size="large"
                icon={<PlusOutlined />}
              >
                {isCreateMode ? "Create" : "Save"}
              </Button>
            </>
          }
        />
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item name="_id" hidden></Form.Item>
            <Form.Item name="aiGenerateTitle" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name="aiGenerateDescription" hidden>
              <Input type="hidden" />
            </Form.Item>

            <Divider orientation="left">Lesson settings</Divider>

            <Row gutter={16} align="top" style={{ marginBottom: 16 }}>
              <Col xs={24} md={10}>
                <Form.Item
                  name={"topic"}
                  label="Topic"
                  rules={[{ required: true, message: "Please select a topic" }]}
                >
                  <Select
                    placeholder="Select topic"
                    options={CHAT_LESSON_TOPIC_OPTIONS}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item
                  name={"estimatedDuration"}
                  label="Estimated duration (min)"
                  rules={[{ required: false }]}
                >
                  <InputNumber
                    min={1}
                    max={60}
                    style={{ width: "100%" }}
                    placeholder="e.g., 3"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={9}>
                <Form.Item label=" " colon={false}>
                  <Space size={16} align="center" wrap>
                    <Form.Item
                      name={"isPremium"}
                      valuePropName="checked"
                      noStyle
                      rules={[]}
                    >
                      <Checkbox>Is Premium</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name={"isActive"}
                      valuePropName="checked"
                      noStyle
                      rules={[]}
                    >
                      <Checkbox>Is Active</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name={"isForJourney"}
                      valuePropName="checked"
                      noStyle
                      rules={[]}
                    >
                      <Checkbox>For journey</Checkbox>
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} align="top">
              <Col xs={24} sm={8}>
                <Form.Item
                  name={["xpRewardByLevel", "beginner"]}
                  label="Beginner XP"
                  rules={[{ required: true, message: "Enter beginner XP" }]}
                >
                  <InputNumber min={0} max={1000} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={["xpRewardByLevel", "intermediate"]}
                  label="Intermediate XP"
                  rules={[{ required: true, message: "Enter intermediate XP" }]}
                >
                  <InputNumber min={0} max={1000} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={["xpRewardByLevel", "advanced"]}
                  label="Advanced XP"
                  rules={[{ required: true, message: "Enter advanced XP" }]}
                >
                  <InputNumber min={0} max={1000} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Learner-facing copy</Divider>
            <div style={{ ...METADATA_SECTION_STYLE, marginTop: 0 }}>
              <div style={METADATA_SECTION_LABEL_STYLE}>
                Title and description per language — English title is used in
                admin lists
              </div>
              <Tabs
                activeKey={activeLanguageTab}
                onChange={setActiveLanguageTab}
                tabBarExtraContent={renderMetadataTranslateButton({
                  loading: translatingLessonMetadata,
                  onClick: handleTranslateLessonMetadata,
                  tooltip:
                    "Translate English title and description to es, fr, de, it, tr, ja, ko, zh",
                })}
                items={CHAT_LESSON_LANGUAGES.map((lang) => ({
                  key: lang.key,
                  label: lang.label,
                  forceRender: true,
                  children: (
                    <>
                      <Form.Item
                        name={["metadata", lang.key, "title"]}
                        label="Title"
                        rules={[
                          {
                            required: lang.key === "en",
                            message: "Please enter a title",
                          },
                        ]}
                      >
                        <Input placeholder={`Title in ${lang.label}`} />
                      </Form.Item>
                      <Form.Item
                        name={["metadata", lang.key, "description"]}
                        label="Description"
                      >
                        <Input.TextArea
                          rows={4}
                          placeholder={`Description in ${lang.label}`}
                        />
                      </Form.Item>
                    </>
                  ),
                }))}
              />
            </div>

            <Divider orientation="left">Images</Divider>
            <Row gutter={16} align="top">
              <Col xs={24} sm={12}>
                <Form.Item label="Thumbnail">
                  <Space align="start" size={12} wrap>
                    {renderFileManagerButton(() =>
                      openFileManager({ type: "thumbnail" }),
                    )}
                    {thumbnail?.md && (
                      <>
                        <img
                          src={mediaPreviewSrc(thumbnail.md)}
                          alt="Thumbnail"
                          style={{
                            maxHeight: 72,
                            maxWidth: 120,
                            borderRadius: 8,
                            objectFit: "cover",
                          }}
                        />
                        <Button
                          danger
                          type="link"
                          size="small"
                          onClick={() => setThumbnail(undefined)}
                        >
                          Clear
                        </Button>
                      </>
                    )}
                  </Space>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Avatar">
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                        width: "100%",
                      }}
                    >
                      <Form.Item name="avatarPrompt" noStyle style={{ flex: 1 }}>
                        <Input.TextArea
                          rows={2}
                          placeholder="DALL-E prompt for avatar generation…"
                        />
                      </Form.Item>
                      {renderFileManagerButton(() =>
                        openFileManager({ type: "avatar" }),
                      )}
                    </div>
                    {avatar?.md && (
                      <Space align="start" size={12} wrap>
                        <img
                          src={mediaPreviewSrc(avatar.md)}
                          alt="Avatar"
                          style={{
                            width: 72,
                            height: 72,
                            objectFit: "cover",
                            borderRadius: "50%",
                          }}
                        />
                        <Button
                          danger
                          type="link"
                          size="small"
                          onClick={() => setAvatar(undefined)}
                        >
                          Clear
                        </Button>
                      </Space>
                    )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Introduction</Divider>
            <Form.Item
              name="introduction"
              label="Introduction text"
              tooltip="Shown before the chat role-play (not part of steps)."
            >
              <Input.TextArea
                rows={4}
                placeholder="Optional intro copy for the learner…"
              />
            </Form.Item>

            <Divider orientation="left">Chat steps</Divider>

            <Form.List name="steps">
              {(fields, { add, remove }) => (
                <>
                  <Collapse
                    accordion
                    activeKey={String(activeStepIndex)}
                    onChange={(k) => {
                      const raw = Array.isArray(k) ? k[0] : k;
                      const idx = Number(raw);
                      if (Number.isFinite(idx) && idx >= 0) {
                        setActiveStepIndex(idx);
                      } else {
                        setActiveStepIndex(0);
                      }
                    }}
                  >
                    {fields.map(({ key, name, ...restField }) => (
                      <Panel
                        header={getStepDisplayTitle(name as number)}
                        key={String(name)}
                        extra={
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(name);
                            }}
                          >
                            Remove
                          </Button>
                        }
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "type"]}
                          hidden
                          initialValue="chat"
                        >
                          <Input type="hidden" />
                        </Form.Item>
                        <div style={METADATA_SECTION_STYLE}>
                          <div style={METADATA_SECTION_LABEL_STYLE}>
                            Step metadata (shown to learners per language)
                          </div>
                          <Tabs
                            activeKey={getStepLanguageTab(name as number)}
                            onChange={(key) =>
                              setStepLanguageTab(name as number, key)
                            }
                            tabBarExtraContent={renderMetadataTranslateButton({
                              loading:
                                translatingStepMetadataIndex ===
                                (name as number),
                              onClick: () =>
                                handleTranslateStepMetadata(name as number),
                              tooltip:
                                "Translate English step title and starter message to es, fr, de, it, tr, ja, ko, zh",
                            })}
                            items={CHAT_LESSON_LANGUAGES.map((lang) => {
                              const stepVoices =
                                voicesByLang[lang.voiceCode] ?? [];
                              const loadingStepVoices =
                                loadingVoicesByLang[lang.voiceCode] ?? false;
                              return {
                                key: lang.key,
                                label: lang.label,
                                forceRender: true,
                                children: (
                                  <>
                                    <Row gutter={16} align="middle">
                                      <Col xs={24} md={12}>
                                        <Form.Item
                                          {...restField}
                                          name={[
                                            name,
                                            "metadata",
                                            lang.key,
                                            "title",
                                          ]}
                                          label="Step title"
                                          rules={[
                                            {
                                              required: lang.key === "en",
                                              message: "Please enter step title",
                                            },
                                          ]}
                                        >
                                          <Input
                                            placeholder={`Step ${name + 1} in ${lang.label}`}
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col xs={24} md={12}>
                                        <Form.Item
                                          label="Step voice"
                                          tooltip="Optional voice for this step."
                                        >
                                          <Space.Compact style={{ width: "100%" }}>
                                            <Form.Item
                                              {...restField}
                                              name={[
                                                name,
                                                "metadata",
                                                lang.key,
                                                "voiceName",
                                              ]}
                                              noStyle
                                            >
                                              <Select
                                                placeholder="Select voice"
                                                loading={loadingStepVoices}
                                                disabled={!stepVoices.length}
                                                style={{ width: "100%" }}
                                                options={stepVoices.map(
                                                  (voice) => ({
                                                    label: `${voice.name} (${voice.ssmlGender})`,
                                                    value: voice.name,
                                                  }),
                                                )}
                                                showSearch
                                                optionFilterProp="label"
                                                onChange={(value) =>
                                                  handleStepVoiceChange(
                                                    name as number,
                                                    lang.key,
                                                    value as string | undefined,
                                                  )
                                                }
                                              />
                                            </Form.Item>
                                            <Button
                                              icon={<PlayCircleOutlined />}
                                              loading={
                                                playingStepVoiceIndex ===
                                                (name as number)
                                              }
                                              disabled={!stepVoices.length}
                                              onClick={() =>
                                                playStepVoice(
                                                  name as number,
                                                  lang.key,
                                                  form.getFieldValue([
                                                    "steps",
                                                    name,
                                                    "metadata",
                                                    lang.key,
                                                    "voiceName",
                                                  ]) as string | undefined,
                                                )
                                              }
                                              title="Play step voice"
                                            />
                                          </Space.Compact>
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                    <Form.Item
                                      {...restField}
                                      name={[
                                        name,
                                        "metadata",
                                        lang.key,
                                        "starterText",
                                      ]}
                                      label="Starter message (AI)"
                                      tooltip="First assistant line in chat (e.g. a greeting)."
                                    >
                                      <Input.TextArea
                                        rows={2}
                                        placeholder={`Starter message in ${lang.label}`}
                                      />
                                    </Form.Item>
                                  </>
                                ),
                              };
                            })}
                          />
                        </div>
                        <Row gutter={16} align="top">
                          <Col xs={24} lg={12}>
                            <Form.Item
                              {...restField}
                              name={[name, "character"]}
                              label="Character"
                            >
                              <Input placeholder="Character" />
                            </Form.Item>
                            <Form.Item
                              label="Step template"
                              tooltip="Template used to generate AI instruction for this step."
                            >
                              <Space.Compact style={{ width: "100%" }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "courseTemplateId"]}
                                  noStyle
                                >
                                  <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    style={{ minWidth: 0, flex: 1 }}
                                    placeholder="Select template"
                                    loading={loadingCourseTemplates}
                                    options={courseTemplateOptions}
                                  />
                                </Form.Item>
                                <Tooltip title="Manage templates in settings">
                                  <Button
                                    type="default"
                                    icon={<SettingOutlined />}
                                    onClick={() =>
                                      navigate(
                                        "/panel/settings?tab=course-templates",
                                      )
                                    }
                                  />
                                </Tooltip>
                              </Space.Compact>
                            </Form.Item>
                            <Form.Item label="Step avatar">
                              <Space align="start" size={12} wrap>
                                {renderFileManagerButton(() =>
                                  openFileManager({
                                    type: "stepAvatar",
                                    stepIndex: name as number,
                                  }),
                                )}
                                <Form.Item
                                  {...restField}
                                  name={[name, "avatar"]}
                                  hidden
                                >
                                  <Input type="hidden" />
                                </Form.Item>
                                {(() => {
                                  const stepAvatar = form.getFieldValue([
                                    "steps",
                                    name,
                                    "avatar",
                                  ]) as SizedImageUrls | undefined;
                                  const avatarUrl =
                                    stepAvatar?.md ?? stepAvatar?.sm;
                                  if (!avatarUrl) return null;
                                  return (
                                    <>
                                      <img
                                        src={mediaPreviewSrc(avatarUrl)}
                                        alt={`Step ${name + 1} avatar`}
                                        style={{
                                          width: 72,
                                          height: 72,
                                          objectFit: "cover",
                                          borderRadius: "50%",
                                        }}
                                      />
                                      <Button
                                        danger
                                        type="link"
                                        size="small"
                                        onClick={() =>
                                          form.setFieldValue(
                                            ["steps", name, "avatar"],
                                            undefined,
                                          )
                                        }
                                      >
                                        Clear
                                      </Button>
                                    </>
                                  );
                                })()}
                              </Space>
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "inputTypes"]}
                              label="Input types"
                              tooltip="How the learner can answer in this step (app)."
                            >
                              <Select
                                mode="multiple"
                                style={{ width: "100%" }}
                                options={[
                                  { label: "Voice", value: "voice" },
                                  { label: "Text", value: "text" },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} lg={12}>
                            <Form.Item
                              {...restField}
                              name={[name, "context"]}
                              label="Context"
                              tooltip="Scene for this step; fills {{context}} in the course template."
                            >
                              <Input.TextArea
                                rows={10}
                                placeholder="Optional setting for this step…"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              valuePropName="checked"
                              name={[name, "canSkip"]}
                              label="Can Skip"
                            >
                      <Checkbox disabled={!canManageActivation} />
                            </Form.Item>
                            <Form.Item label=" ">
                              <Checkbox
                                checked={
                                  sameVoiceAcrossSteps[name as number] ??
                                  false
                                }
                                onChange={(e) =>
                                  handleSameVoiceToggle(
                                    name as number,
                                    e.target.checked,
                                  )
                                }
                              >
                                Same voice for all steps (all languages)
                              </Checkbox>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Divider orientation="left" plain>
                          Tasks by level
                        </Divider>
                        <div
                          style={{
                            color: "rgba(0,0,0,0.45)",
                            marginBottom: 12,
                          }}
                        >
                          Learner checklist fills {"{{user_tasks}}"}; AI-side
                          rules fill {"{{ai_tasks}}"}. Resolved from the preview
                          level with the same fallbacks as the live lesson.
                        </div>
                        {(
                          [
                            ["beginner", "Beginner"],
                            ["intermediate", "Intermediate"],
                            ["advanced", "Advanced"],
                          ] as const
                        ).map(([levelKey, levelLabel]) => (
                          <div key={levelKey}>
                            <Divider orientation="left" plain>
                              {levelLabel}
                            </Divider>
                            <Row gutter={[16, 16]} align="top">
                              <Col xs={24} lg={12}>
                                <div
                                  style={{
                                    marginBottom: 8,
                                    fontWeight: 500,
                                    color: "rgba(0, 0, 0, 0.88)",
                                  }}
                                >
                                  User
                                </div>
                                <Form.List
                                  name={[
                                    name,
                                    "tasksByLevel",
                                    levelKey,
                                    "user",
                                  ]}
                                >
                                  {(
                                    taskFields,
                                    {
                                      add: addTask,
                                      remove: removeTask,
                                      move: moveTask,
                                    },
                                  ) => (
                                    <>
                                      {(() => {
                                        const dragType = `chat-step-${name}-${levelKey}-user`;
                                        return taskFields.map((taskField) => (
                                          <div
                                            key={taskField.key}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              marginBottom: 8,
                                              width: "100%",
                                            }}
                                            onDragOver={(event) => {
                                              event.preventDefault();
                                            }}
                                            onDrop={(event) => {
                                              event.preventDefault();
                                              const sourceType =
                                                event.dataTransfer.getData(
                                                  "application/x-chat-task-list",
                                                );
                                              if (sourceType !== dragType)
                                                return;
                                              const sourceIndex = Number(
                                                event.dataTransfer.getData(
                                                  "text/plain",
                                                ),
                                              );
                                              const targetIndex =
                                                taskField.name;
                                              if (
                                                Number.isFinite(sourceIndex) &&
                                                sourceIndex !== targetIndex
                                              ) {
                                                moveTask(
                                                  sourceIndex,
                                                  targetIndex,
                                                );
                                              }
                                            }}
                                          >
                                            <Button
                                              type="text"
                                              icon={<HolderOutlined />}
                                              style={{
                                                cursor: "grab",
                                                color: "rgba(0,0,0,0.45)",
                                                flexShrink: 0,
                                              }}
                                              draggable
                                              onDragStart={(event) => {
                                                event.dataTransfer.setData(
                                                  "application/x-chat-task-list",
                                                  dragType,
                                                );
                                                event.dataTransfer.setData(
                                                  "text/plain",
                                                  String(taskField.name),
                                                );
                                                event.dataTransfer.effectAllowed =
                                                  "move";
                                              }}
                                              onDragEnd={(event) => {
                                                event.currentTarget.style.cursor =
                                                  "grab";
                                              }}
                                              title="Drag to reorder"
                                            />
                                            <div
                                              style={{
                                                flex: 1,
                                                minWidth: 0,
                                              }}
                                            >
                                              <Form.Item
                                                {...taskField}
                                                noStyle
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Enter learner task",
                                                  },
                                                ]}
                                              >
                                                <Input
                                                  placeholder={`Learner task (${levelLabel})`}
                                                  style={{ width: "100%" }}
                                                />
                                              </Form.Item>
                                            </div>
                                            <Button
                                              type="link"
                                              danger
                                              icon={<DeleteOutlined />}
                                              style={{ flexShrink: 0 }}
                                              onClick={() =>
                                                removeTask(taskField.name)
                                              }
                                            />
                                          </div>
                                        ));
                                      })()}
                                      <Button
                                        type="dashed"
                                        onClick={() => addTask()}
                                        block
                                        icon={<PlusOutlined />}
                                      >
                                        Add {levelLabel} user task
                                      </Button>
                                    </>
                                  )}
                                </Form.List>
                              </Col>
                              <Col xs={24} lg={12}>
                                <div
                                  style={{
                                    marginBottom: 8,
                                    fontWeight: 500,
                                    color: "rgba(0, 0, 0, 0.88)",
                                  }}
                                >
                                  AI
                                </div>
                                <Form.List
                                  name={[name, "tasksByLevel", levelKey, "ai"]}
                                >
                                  {(
                                    aiFields,
                                    {
                                      add: addAi,
                                      remove: removeAi,
                                      move: moveAi,
                                    },
                                  ) => (
                                    <>
                                      {(() => {
                                        const dragType = `chat-step-${name}-${levelKey}-ai`;
                                        return aiFields.map((aiField) => (
                                          <div
                                            key={aiField.key}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              marginBottom: 8,
                                              width: "100%",
                                            }}
                                            onDragOver={(event) => {
                                              event.preventDefault();
                                            }}
                                            onDrop={(event) => {
                                              event.preventDefault();
                                              const sourceType =
                                                event.dataTransfer.getData(
                                                  "application/x-chat-task-list",
                                                );
                                              if (sourceType !== dragType)
                                                return;
                                              const sourceIndex = Number(
                                                event.dataTransfer.getData(
                                                  "text/plain",
                                                ),
                                              );
                                              const targetIndex = aiField.name;
                                              if (
                                                Number.isFinite(sourceIndex) &&
                                                sourceIndex !== targetIndex
                                              ) {
                                                moveAi(
                                                  sourceIndex,
                                                  targetIndex,
                                                );
                                              }
                                            }}
                                          >
                                            <Button
                                              type="text"
                                              icon={<HolderOutlined />}
                                              style={{
                                                cursor: "grab",
                                                color: "rgba(0,0,0,0.45)",
                                                flexShrink: 0,
                                              }}
                                              draggable
                                              onDragStart={(event) => {
                                                event.dataTransfer.setData(
                                                  "application/x-chat-task-list",
                                                  dragType,
                                                );
                                                event.dataTransfer.setData(
                                                  "text/plain",
                                                  String(aiField.name),
                                                );
                                                event.dataTransfer.effectAllowed =
                                                  "move";
                                              }}
                                              onDragEnd={(event) => {
                                                event.currentTarget.style.cursor =
                                                  "grab";
                                              }}
                                              title="Drag to reorder"
                                            />
                                            <div
                                              style={{
                                                flex: 1,
                                                minWidth: 0,
                                              }}
                                            >
                                              <Form.Item
                                                {...aiField}
                                                noStyle
                                                rules={[
                                                  {
                                                    required: true,
                                                    message: "Enter AI rule",
                                                  },
                                                ]}
                                              >
                                                <Input
                                                  placeholder={`AI rule (${levelLabel})`}
                                                  style={{ width: "100%" }}
                                                />
                                              </Form.Item>
                                            </div>
                                            <Button
                                              type="link"
                                              danger
                                              icon={<DeleteOutlined />}
                                              style={{ flexShrink: 0 }}
                                              onClick={() =>
                                                removeAi(aiField.name)
                                              }
                                            />
                                          </div>
                                        ));
                                      })()}
                                      <Button
                                        type="dashed"
                                        onClick={() => addAi()}
                                        block
                                        icon={<PlusOutlined />}
                                      >
                                        Add {levelLabel} AI rule
                                      </Button>
                                    </>
                                  )}
                                </Form.List>
                              </Col>
                            </Row>
                          </div>
                        ))}

                      </Panel>
                    ))}
                  </Collapse>
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        type: "chat",
                        metadata: {
                          en: { title: "New step" },
                        },
                        tasksByLevel: {
                          beginner: { user: [], ai: [] },
                          intermediate: { user: [], ai: [] },
                          advanced: { user: [], ai: [] },
                        },
                        inputTypes: ["voice", "text"],
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 16 }}
                  >
                    Add chat step
                  </Button>
                </>
              )}
            </Form.List>
          </Col>
        </Row>

        {!isPreviewPanelOpen && (
          <>
            <div
              aria-hidden
              style={{
                position: "fixed",
                right: 0,
                top: 0,
                bottom: 0,
                width: 28,
                pointerEvents: "none",
                zIndex: 999,
                background:
                  "linear-gradient(to left, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.02) 55%, transparent)",
                boxShadow: "inset 6px 0 18px -8px rgba(0, 0, 0, 0.22)",
              }}
            />
            <Tooltip title="Open course template preview" placement="left">
              <button
                type="button"
                aria-label="Open course template preview"
                onClick={() => setIsPreviewPanelOpen(true)}
                style={{
                  position: "fixed",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1000,
                  width: 32,
                  height: 112,
                  padding: 0,
                  border: "1px solid #d9d9d9",
                  borderRight: "none",
                  borderRadius: "10px 0 0 10px",
                  background: "#fff",
                  boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.12)",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  color: "#1677ff",
                }}
              >
                <LeftOutlined style={{ fontSize: 14 }} />
                <span
                  style={{
                    writingMode: "vertical-rl",
                    textOrientation: "mixed",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    color: "rgba(0, 0, 0, 0.65)",
                  }}
                >
                  Template
                </span>
              </button>
            </Tooltip>
          </>
        )}

        <Drawer
          title="Course template"
          placement="right"
          width="min(860px, 90vw)"
          open={isPreviewPanelOpen}
          onClose={() => setIsPreviewPanelOpen(false)}
          destroyOnClose={false}
        >
          {!selectedTemplate ? (
            <div style={{ color: "rgba(0,0,0,0.45)" }}>
              Select a course template on a chat step to preview or edit its
              source.
            </div>
          ) : (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div style={{ color: "rgba(0,0,0,0.65)" }}>
                <strong>{selectedTemplate.name}</strong>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ color: "rgba(0,0,0,0.65)" }}>
                  Preview placeholders as level:
                </span>
                <Select<CourseLevelKey>
                  style={{ minWidth: 168 }}
                  value={previewTaskLevel}
                  onChange={(v) => setPreviewTaskLevel(v)}
                  options={[
                    { value: "beginner", label: "Beginner" },
                    { value: "intermediate", label: "Intermediate" },
                    { value: "advanced", label: "Advanced" },
                  ]}
                />
              </div>
              <Tabs
                defaultActiveKey="preview"
                items={[
                  {
                    key: "preview",
                    label: "Preview",
                    children: (
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          background: "#fafafa",
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: 12,
                          maxHeight: "calc(100vh - 280px)",
                          overflow: "auto",
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          fontSize: 12,
                          lineHeight: 1.45,
                        }}
                      >
                        {!templateDraft.trim()
                          ? "Template is empty."
                          : renderHighlightedCourseTemplatePreview(
                              templateDraft,
                              previewVars as Record<string, string>,
                            )}
                      </div>
                    ),
                  },
                  {
                    key: "source",
                    label: "Template source",
                    children: (
                      <Space
                        direction="vertical"
                        size={10}
                        style={{ width: "100%" }}
                      >
                        <div style={COURSE_TEMPLATE_PLACEHOLDERS_HINT_STYLE}>
                          Placeholders: {COURSE_TEMPLATE_PLACEHOLDERS_TEXT}
                        </div>
                        <Input.TextArea
                          value={templateDraft}
                          onChange={(e) => setTemplateDraft(e.target.value)}
                          autoSize={{ minRows: 16, maxRows: 36 }}
                          style={{
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: 12,
                            lineHeight: 1.45,
                          }}
                          placeholder="System prompt template…"
                        />
                        <Button
                          type="primary"
                          loading={savingTemplate}
                          onClick={handleSaveCourseTemplate}
                        >
                          Save template
                        </Button>
                      </Space>
                    ),
                  },
                ]}
              />
            </Space>
          )}
        </Drawer>

        {aiGeneratePanelOpen && (
          <div
            ref={aiGeneratePanelRef}
            style={{
              position: "fixed",
              width: "min(980px, calc(100vw - 32px))",
              zIndex: 1001,
              borderRadius: aiGeneratePanelPos ? 12 : "12px 12px 0 0",
              border: "1px solid #d9d9d9",
              borderBottom: aiGeneratePanelPos ? "1px solid #d9d9d9" : "none",
              background: "#fff",
              boxShadow: aiGeneratePanelPos
                ? "0 12px 32px rgba(0, 0, 0, 0.16)"
                : "0 -8px 28px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
              touchAction: "none",
              ...(aiGeneratePanelPos
                ? {
                    left: aiGeneratePanelPos.left,
                    top: aiGeneratePanelPos.top,
                  }
                : {
                    left: "50%",
                    bottom: 0,
                    transform: "translateX(-50%)",
                  }),
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderBottom: aiGeneratePanelExpanded
                  ? "1px solid #f0f0f0"
                  : undefined,
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
                cursor: aiGeneratePanelDragging ? "grabbing" : "grab",
                userSelect: "none",
              }}
              onPointerDown={handleAiGenerateHeaderPointerDown}
              onPointerMove={handleAiGenerateHeaderPointerMove}
              onPointerUp={handleAiGenerateHeaderPointerUp}
              onPointerCancel={handleAiGenerateHeaderPointerUp}
            >
              <HolderOutlined
                style={{ color: "rgba(0,0,0,0.35)", fontSize: 14, flexShrink: 0 }}
              />
              <RobotOutlined style={{ color: "#667eea", fontSize: 16 }} />
              <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>
                AI lesson generator
              </span>
              {generating && (
                <span style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                  Generating…
                </span>
              )}
              <Tooltip
                title={aiGeneratePanelExpanded ? "Collapse" : "Expand"}
              >
                <Button
                  type="text"
                  size="small"
                  aria-label={
                    aiGeneratePanelExpanded ? "Collapse panel" : "Expand panel"
                  }
                  icon={
                    aiGeneratePanelExpanded ? <DownOutlined /> : <UpOutlined />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setAiGeneratePanelExpanded((v) => !v);
                  }}
                />
              </Tooltip>
              <Tooltip title="Close">
                <Button
                  type="text"
                  size="small"
                  aria-label="Close AI generator panel"
                  icon={<CloseOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAiGeneratePanelOpen(false);
                  }}
                />
              </Tooltip>
            </div>
            {aiGeneratePanelExpanded && (
              <div
                style={{
                  maxHeight: "min(75vh, 640px)",
                  overflow: "auto",
                  padding: "14px 16px 16px",
                }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                    Describe the lesson scenario and what each chat step should
                    cover. The form stays editable while this panel is open.
                  </div>
                  <div>
                    <div
                      style={{
                        marginBottom: 6,
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      Lesson title
                    </div>
                    <Input
                      value={aiGenerateTitle}
                      onChange={(e) => setAiGenerateTitle(e.target.value)}
                      placeholder="e.g. Ordering Coffee at a Café"
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        marginBottom: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        Description & step guidance
                      </div>
                      <Checkbox
                        checked={aiGenerateDescriptionRtl}
                        onChange={(e) =>
                          setAiGenerateDescriptionRtl(e.target.checked)
                        }
                      >
                        RTL
                      </Checkbox>
                    </div>
                    <Input.TextArea
                      value={aiGenerateDescription}
                      onChange={(e) =>
                        setAiGenerateDescription(e.target.value)
                      }
                      rows={10}
                      dir={aiGenerateDescriptionRtl ? "rtl" : "ltr"}
                      style={{
                        textAlign: aiGenerateDescriptionRtl ? "right" : "left",
                      }}
                      placeholder="e.g. 3 steps: greet the barista, order a drink with customizations, handle payment. Friendly barista character, casual café setting…"
                    />
                  </div>
                  <Button
                    type="primary"
                    icon={<RobotOutlined />}
                    loading={generating}
                    onClick={handleAiGenerate}
                    block
                    style={{
                      background: generating
                        ? undefined
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderColor: "transparent",
                    }}
                  >
                    {generating ? "Generating lesson…" : "Generate lesson"}
                  </Button>
                </Space>
              </div>
            )}
          </div>
        )}

        <Modal
          title="File manager"
          open={fileManagerOpen}
          onCancel={() => {
            setFileManagerOpen(false);
            setFileManagerTarget(null);
          }}
          footer={null}
          width="min(1320px, 96vw)"
          style={{ top: 24 }}
          bodyStyle={{ maxHeight: "calc(100vh - 180px)", overflow: "auto" }}
          destroyOnClose
        >
          <AdminFileManager
            defaultFolder="images/lessons"
            onSelectUrl={handleFileManagerSelect}
          />
        </Modal>
      </Form>
    </Card>
  );
}
